import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../app/trpc.js";
import { Badge, Button, Card, FormRow, Input, Notice, PageTitle, SectionTitle, Select, Spinner } from "../../ui/index.js";
import { useCabinet } from "./context.js";

/** Команда салона и приглашения мастеров (ссылка на 7 дней, привязка к мастеру календаря). Только владелец. */
export function TeamPage() {
  const { t } = useTranslation();
  const cab = useCabinet();
  const team = useQuery(trpc.cabinet.team.list.queryOptions({ salonId: cab.salonId }, { enabled: cab.isOwner }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId }));
  const [masterId, setMasterId] = useState("");
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const invite = useMutation(trpc.auth.createInvite.mutationOptions());

  if (team.isPending || masters.isPending) return <Spinner />;
  const masterName = (id: string | null) => masters.data?.find((m) => m.id === id)?.name;
  const linked = new Set(team.data?.map((m) => m.masterId).filter(Boolean));

  const share = async (url: string) => {
    setCopied(false);
    try {
      if (navigator.share) await navigator.share({ url, title: t("team.shareTitle") });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch {
      // пользователь закрыл окно «поделиться»
    }
  };

  return (
    <div>
      <PageTitle>{t("team.title")}</PageTitle>
      {team.isError && <Notice kind="error">{t("common.error")}</Notice>}
      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        <Card className="py-1">
          {team.data?.map((m) => (
            <div key={m.userId} className="flex items-center justify-between border-b border-slate-100 py-3 last:border-b-0">
              <div className="min-w-0">
                <div className="truncate">{m.name}</div>
                <div className="truncate text-sm text-slate-500">
                  {m.email}
                  {m.masterId && ` · ${t("team.calendarOf", { name: masterName(m.masterId) ?? "?" })}`}
                </div>
              </div>
              <Badge tone={m.role === "owner" ? "blue" : "slate"}>{t(`invite.roles.${m.role}`)}</Badge>
            </div>
          ))}
        </Card>

        <div>
          <SectionTitle className="lg:mt-0">{t("team.inviteTitle")}</SectionTitle>
          <Card>
            <p className="mb-3 text-sm text-slate-600">{t("team.inviteHint")}</p>
            <div className="space-y-3">
              <FormRow label={t("team.master")} htmlFor="inv-master">
                <Select id="inv-master" value={masterId} onChange={(e) => setMasterId(e.target.value)}>
                  <option value="">{t("team.noMaster")}</option>
                  {masters.data
                    ?.filter((m) => !linked.has(m.id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </Select>
              </FormRow>
              <FormRow label={t("team.email")} htmlFor="inv-email">
                <Input id="inv-email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marko@example.com" />
              </FormRow>
              <Button
                className="w-full lg:w-auto"
                disabled={invite.isPending}
                onClick={() => invite.mutate({ salonId: cab.salonId, masterId: masterId || null, email: email.trim() || null })}
              >
                {t("team.createLink")}
              </Button>
              {invite.isError && <Notice kind="error">{t("common.error")}</Notice>}
              {invite.data && (
                <div className="space-y-2">
                  <Notice kind="success">{t("team.linkReady")}</Notice>
                  <div className="break-all rounded-xl bg-slate-50 p-3 text-sm">{invite.data.url}</div>
                  <Button variant="secondary" className="w-full lg:w-auto" onClick={() => void share(invite.data.url)}>
                    {t("team.share")}
                  </Button>
                  {copied && <p className="text-sm text-green-700">{t("team.copied")}</p>}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
